import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dashboardService } from '../../services/dashboardService';

const initialState = {
  stats: {
    totalLeads: 0,
    totalCustomers: 0,
    vehiclesInStock: 0,
    serviceAppointments: 0,
    monthlyRevenue: 0,
    conversionRate: 0,
  },
  salesChartData: null,
  leadSourcesData: null,
  inventoryBreakdown: null,
  recentActivities: [],
  upcomingTasks: [],
  performanceMetrics: null,
  loading: false,
  error: null,
};

// Dashboard thunks
export const fetchDashboardStats = createAsyncThunk(
  'dashboard/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const data = await dashboardService.getDashboardStats();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch dashboard stats');
    }
  }
);

export const fetchSalesChartData = createAsyncThunk(
  'dashboard/fetchSalesChartData',
  async (period = '6m', { rejectWithValue }) => {
    try {
      const data = await dashboardService.getSalesChartData(period);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch sales chart data');
    }
  }
);

export const fetchLeadSourcesData = createAsyncThunk(
  'dashboard/fetchLeadSourcesData',
  async (_, { rejectWithValue }) => {
    try {
      const data = await dashboardService.getLeadSourcesData();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch lead sources data');
    }
  }
);

export const fetchInventoryBreakdown = createAsyncThunk(
  'dashboard/fetchInventoryBreakdown',
  async (_, { rejectWithValue }) => {
    try {
      const data = await dashboardService.getInventoryBreakdown();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch inventory breakdown');
    }
  }
);

export const fetchRecentActivities = createAsyncThunk(
  'dashboard/fetchRecentActivities',
  async (limit = 10, { rejectWithValue }) => {
    try {
      const data = await dashboardService.getRecentActivities(limit);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch recent activities');
    }
  }
);

export const fetchUpcomingTasks = createAsyncThunk(
  'dashboard/fetchUpcomingTasks',
  async (_, { rejectWithValue }) => {
    try {
      const data = await dashboardService.getUpcomingTasks();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch upcoming tasks');
    }
  }
);

export const fetchPerformanceMetrics = createAsyncThunk(
  'dashboard/fetchPerformanceMetrics',
  async (period = 'month', { rejectWithValue }) => {
    try {
      const data = await dashboardService.getPerformanceMetrics(period);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch performance metrics');
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetDashboard: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard stats
      .addCase(fetchDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Sales chart data
      .addCase(fetchSalesChartData.fulfilled, (state, action) => {
        state.salesChartData = action.payload;
      })
      // Lead sources data
      .addCase(fetchLeadSourcesData.fulfilled, (state, action) => {
        state.leadSourcesData = action.payload;
      })
      // Inventory breakdown
      .addCase(fetchInventoryBreakdown.fulfilled, (state, action) => {
        state.inventoryBreakdown = action.payload;
      })
      // Recent activities
      .addCase(fetchRecentActivities.fulfilled, (state, action) => {
        state.recentActivities = action.payload;
      })
      // Upcoming tasks
      .addCase(fetchUpcomingTasks.fulfilled, (state, action) => {
        state.upcomingTasks = action.payload;
      })
      // Performance metrics
      .addCase(fetchPerformanceMetrics.fulfilled, (state, action) => {
        state.performanceMetrics = action.payload;
      });
  },
});

export const { clearError, resetDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;